import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { getLocalStorageData } from '../shared/utils/localStorage';
import request from '../request';

interface SosSocketContextType {
  sosTriggered: boolean;
  lastSosAlert: any;
  clearSos: () => void;
}

const SosSocketContext = createContext<SosSocketContextType>({
  sosTriggered: false,
  lastSosAlert: null,
  clearSos: () => {},
});

export const useSosSocket = () => useContext(SosSocketContext);

const playBeep = () => {
  try {
    const audioCtx = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
    gainNode.gain.setValueAtTime(1, audioCtx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.001,
      audioCtx.currentTime + 1,
    );
    oscillator.start(audioCtx.currentTime);
    oscillator.stop(audioCtx.currentTime + 1);
  } catch (e) {
    console.warn('Audio playback failed:', e);
  }
};

const showBrowserNotification = () => {
  if (Notification.permission === 'granted') {
    new Notification('SOS Alert!', {
      body: 'A new SOS alert has been received.',
      icon: '/favicon.ico',
    });
  }
};

export const SosSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [sosTriggered, setSosTriggered] = useState(false);
  const [lastSosAlert, setLastSosAlert] = useState<any>(null);
  const socketRef = useRef<Socket | null>(null);

  const clearSos = () => setSosTriggered(false);

  useEffect(() => {
    const userData = getLocalStorageData('userData');
    if (userData?.userType === 'SuperAdmin') {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    // Request browser notification permission
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }

    const connectSocket = () => {
      // Clean up any existing socket before reconnecting
      if (socketRef.current) {
        console.log('[SOS] Disconnecting existing socket before reconnect');
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      const userData = getLocalStorageData('userData');
      const token = getLocalStorageData('token');
      const orgID = userData?.orgID ?? userData?.employee?.orgID;
      const hideNotificationsForSuperAdmin = userData?.userType === 'SuperAdmin';

      console.log('[SOS] Initializing socket context — orgID:', orgID, 'hasToken:', !!token);

      // Skip connection if no orgID (SuperAdmin not acting as org)
      if (!orgID || !token || hideNotificationsForSuperAdmin) {
        console.log('[SOS] Notifications unavailable for this user — skipping socket connection');
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, '');
      console.log('[SOS] Connecting to socket at:', `${apiUrl}/sos`);

      const socket = io(`${apiUrl}/sos`, {
        query: {
          token,
          orgID: String(orgID),
        },
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 3000,
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        console.log('[SOS] Socket connected — ID:', socket.id);
      });

      socket.on('sos-alert', async (data) => {
        console.log('[SOS] sos-alert event received — data:', data);
        setSosTriggered(true);
        setLastSosAlert({ ...data, _receivedAt: Date.now() });

        try {
          const response = await request.get(
            '/admin/config/getConfigValue?configName=SOS Sound Notification',
          );
          const soundEnabled = response?.data;
          console.log('[SOS] Sound config value:', soundEnabled);

          if (soundEnabled === 'Yes') {
            console.log('[SOS] Playing beep sound');
            playBeep();

            if (document.visibilityState !== 'visible') {
              console.log('[SOS] Tab is inactive — also showing browser notification');
              showBrowserNotification();
            }
          } else {
            console.log('[SOS] Sound notification disabled for this org — skipping');
          }
        } catch (e) {
          console.warn('[SOS] Failed to fetch sound config:', e);
        }
        console.log('[SOS] sosTriggered set to true');
      });

      socket.on('disconnect', (reason) => {
        console.log('[SOS] Socket disconnected — reason:', reason);
      });

      socket.on('connect_error', (err) => {
        console.warn('[SOS] Connection error:', err.message);
      });

      socket.on('reconnect_attempt', (attempt) => {
        console.log('[SOS] Reconnection attempt:', attempt);
      });
    };

    // Connect on initial mount
    connectSocket();

    // Reconnect whenever login writes new userData to localStorage
    window.addEventListener('userDataUpdated', connectSocket);

    return () => {
      console.log('[SOS] Cleaning up socket connection');
      window.removeEventListener('userDataUpdated', connectSocket);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  return (
    <SosSocketContext.Provider value={{ sosTriggered, lastSosAlert, clearSos }}>
      {children}
    </SosSocketContext.Provider>
  );
};